import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, Select, MenuItem, FormControl,
  InputLabel, Alert, CircularProgress, IconButton, Tooltip, Paper,
  Switch, TextField, Collapse, Checkbox, FormControlLabel
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SettingsIcon from '@mui/icons-material/Settings';
import { tellihealthApi, apiRequest } from '../services/api';

interface LinkedDevice {
  device_id: string;
  serial_number: string | null;
  active: boolean;
  gen_ver: string | null;
  added_at: string;
  device_type: string | null;
  manufacturer: string | null;
}

interface AvailableDevice {
  device_id: string;
  serial_number: string | null;
  gen_ver: string | null;
  device_type: string | null;
  manufacturer: string | null;
}

interface PatientDeviceSectionProps {
  patientId: string;
  organizationId?: string;
  onDeviceChange?: (device: LinkedDevice | null) => void;
}

const PatientDeviceSection: React.FC<PatientDeviceSectionProps> = ({ patientId, organizationId, onDeviceChange }) => {
  const [linkedDevice, setLinkedDevice] = useState<LinkedDevice | null>(null);
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Device settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [arstart, setArstart] = useState('0800');
  const [arstop, setArstop] = useState('2000');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);
  // PHA-708: feedback on whether the linked IMEI has ever transmitted data.
  const [linkNotice, setLinkNotice] = useState<{ severity: 'success' | 'warning'; msg: string } | null>(null);
  // PHA-1124: claim readings this device produced before anybody linked it —
  // the AJC/SmartMeter case, where data flowed from 09/03 while the devices were
  // still unregistered. Opt-in, and the count is shown before committing.
  const [associateOrphans, setAssociateOrphans] = useState(false);
  const [claimable, setClaimable] = useState<{ count: number; firstTime: string | null } | null>(null);

  const loadDevice = useCallback(async () => {
    try {
      const data = await tellihealthApi.getPatientDevice(patientId);
      const device = data.device || null;
      setLinkedDevice(device);
      onDeviceChange?.(device);
    } catch {
      setLinkedDevice(null);
      onDeviceChange?.(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, onDeviceChange]);

  const loadAvailable = useCallback(async () => {
    if (!organizationId) return;
    try {
      const data = await tellihealthApi.getAvailableDevices(patientId, organizationId);
      setAvailableDevices(data.devices || []);
    } catch {
      setAvailableDevices([]);
    }
  }, [patientId, organizationId]);

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  useEffect(() => {
    if (!linkedDevice) {
      loadAvailable();
    }
  }, [linkedDevice, loadAvailable]);

  // PHA-1124: how many unowned readings the selected device is carrying. Loaded
  // on selection so the checkbox can say what it would actually do; a device
  // with none hides the checkbox entirely rather than offering a no-op.
  useEffect(() => {
    let cancelled = false;
    if (!selectedDeviceId) {
      setClaimable(null);
      setAssociateOrphans(false);
      return undefined;
    }
    tellihealthApi
      .getClaimableOrphanReadings(selectedDeviceId)
      .then((data) => {
        if (cancelled) return;
        setClaimable(data);
        // Default ON when there is something to claim: leaving months of
        // transferred readings stranded is the failure this ticket exists to fix,
        // and an unclaimed reading is invisible everywhere with no error.
        setAssociateOrphans((data?.count || 0) > 0);
      })
      .catch(() => {
        if (!cancelled) setClaimable(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDeviceId]);

  const handleLink = async () => {
    if (!selectedDeviceId) return;
    setLinking(true);
    setError(null);
    setLinkNotice(null);
    try {
      const result = await tellihealthApi.linkDevice(patientId, selectedDeviceId, associateOrphans);
      setSelectedDeviceId('');
      await loadDevice();
      // PHA-708: surface whether this IMEI has ever sent data. count === 0 is the
      // on-the-spot signal that a wrong/mistyped number (or wrong unit) was linked.
      const stats = result?.readingStats;
      // PHA-1124: say explicitly how many previously-orphaned readings were
      // claimed. Silence here is how PHA-708 went unnoticed for weeks.
      const claimed = result?.associatedReadings || 0;
      const claimedFrom = result?.associatedFrom
        ? new Date(result.associatedFrom).toLocaleDateString()
        : null;
      const claimedMsg = claimed > 0
        ? ` ${claimed} previously unassigned reading(s)${claimedFrom ? ` back to ${claimedFrom}` : ''} are now assigned to this patient.`
        : '';
      if (stats) {
        if (stats.count > 0) {
          const last = stats.lastReadingAt ? new Date(stats.lastReadingAt).toLocaleDateString() : 'unknown';
          setLinkNotice({ severity: 'success', msg: `✓ ${stats.count} reading(s) received for this device (last ${last}).${claimedMsg}` });
        } else {
          setLinkNotice({ severity: 'warning', msg: 'No readings have arrived for this IMEI yet. If this is a replacement device, double-check the number matches the unit. For a new device, take a test reading and confirm it appears here within a few minutes — readings sent under a different IMEI will not show up.' });
        }
      }
    } catch {
      setError('Failed to link device');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!linkedDevice) return;
    setLinking(true);
    setError(null);
    try {
      await tellihealthApi.unlinkDevice(patientId, linkedDevice.device_id);
      setLinkedDevice(null);
      onDeviceChange?.(null);
      setSettingsOpen(false);
      await loadAvailable();
    } catch {
      setError('Failed to unlink device');
    } finally {
      setLinking(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!linkedDevice) return;
    setSavingSettings(true);
    setError(null);
    setSettingsSuccess(null);
    try {
      const response = await apiRequest(`/session/tellihealth/devices/${linkedDevice.device_id}/settings`, {
        method: 'POST',
        body: JSON.stringify({ enabled: alertsEnabled, arstart, arstop }),
      });
      await response.json();
      setSettingsSuccess('Settings sent to device (pending confirmation)');
    } catch {
      setError('Failed to update device settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Validate HHMM format
  const isValidTime = (val: string) => /^[0-2][0-9][0-5][0-9]$/.test(val);
  const formatTime = (val: string) => {
    if (val.length === 4) return `${val.slice(0, 2)}:${val.slice(2)}`;
    return val;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading device...</Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>RPM Device</Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {settingsSuccess && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSettingsSuccess(null)}>{settingsSuccess}</Alert>}
      {linkNotice && <Alert severity={linkNotice.severity} sx={{ mb: 1 }} onClose={() => setLinkNotice(null)}>{linkNotice.msg}</Alert>}

      {linkedDevice ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={linkedDevice.device_id}
              color={linkedDevice.active ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
            {linkedDevice.serial_number && (
              <Typography variant="caption" color="text.secondary">
                SN: {linkedDevice.serial_number}
              </Typography>
            )}
            {linkedDevice.gen_ver && (
              <Chip label={`Gen ${linkedDevice.gen_ver}`} size="small" variant="outlined" />
            )}
            {linkedDevice.device_type && (
              <Typography variant="caption" color="text.secondary">
                Type: {linkedDevice.device_type}
              </Typography>
            )}
            {linkedDevice.manufacturer && (
              <Chip label={linkedDevice.manufacturer} size="small" variant="outlined" />
            )}
            <Typography variant="caption" color="text.secondary">
              Linked: {new Date(linkedDevice.added_at).toLocaleDateString()}
            </Typography>
            <Tooltip title="Device settings">
              <IconButton size="small" onClick={() => setSettingsOpen(!settingsOpen)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Unlink device">
              <IconButton size="small" color="error" onClick={handleUnlink} disabled={linking}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Collapse in={settingsOpen}>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>Alert Settings</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">Alerts</Typography>
                  <Switch
                    size="small"
                    checked={alertsEnabled}
                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                  />
                  <Typography variant="caption" color={alertsEnabled ? 'success.main' : 'text.secondary'}>
                    {alertsEnabled ? 'On' : 'Off'}
                  </Typography>
                </Box>
                <TextField
                  label="Start (HHMM)"
                  size="small"
                  value={arstart}
                  onChange={(e) => setArstart(e.target.value)}
                  error={arstart.length === 4 && !isValidTime(arstart)}
                  helperText={arstart.length === 4 ? formatTime(arstart) : ''}
                  sx={{ width: 120 }}
                  disabled={!alertsEnabled}
                />
                <TextField
                  label="Stop (HHMM)"
                  size="small"
                  value={arstop}
                  onChange={(e) => setArstop(e.target.value)}
                  error={arstop.length === 4 && !isValidTime(arstop)}
                  helperText={arstop.length === 4 ? formatTime(arstop) : ''}
                  sx={{ width: 120 }}
                  disabled={!alertsEnabled}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveSettings}
                  disabled={savingSettings || !isValidTime(arstart) || !isValidTime(arstop)}
                >
                  {savingSettings ? 'Sending...' : 'Save'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Settings are sent asynchronously. The device will confirm (ACK/NACK) on next connection.
              </Typography>
            </Box>
          </Collapse>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          {availableDevices.length > 0 ? (
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Select device</InputLabel>
                <Select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  label="Select device"
                >
                  {availableDevices.map((d) => (
                    <MenuItem key={d.device_id} value={d.device_id}>
                      {d.device_id}{d.device_type ? ` (${d.device_type})` : ''}{d.serial_number ? ` SN: ${d.serial_number}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={handleLink}
                disabled={!selectedDeviceId || linking}
              >
                Link
              </Button>
              {/* PHA-1124 req 3: opt-in claim of readings this device sent before
                  it was linked to anyone. Only offered when there is something to
                  claim. Backdating stops at the device's previous patient, so this
                  can never pull one patient's readings onto another (PHA-743). */}
              {claimable && claimable.count > 0 && (
                <Box sx={{ width: '100%' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={associateOrphans}
                        onChange={(e) => setAssociateOrphans(e.target.checked)}
                      />
                    )}
                    label={(
                      <Typography variant="body2">
                        Associate orphan readings on this device
                        {' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({claimable.count} unassigned reading{claimable.count === 1 ? '' : 's'}
                          {claimable.firstTime ? ` since ${new Date(claimable.firstTime).toLocaleDateString()}` : ''})
                        </Typography>
                      </Typography>
                    )}
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No available devices {organizationId ? 'for this organization' : ''}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PatientDeviceSection;
